import {
  DataTable,
  FormDatePicker,
  FormSelect,
  Loader,
  Dialog,
} from "@/components";
import useBOMs from "./useBOMs";
import {
  Button,
  DialogClose,
  DialogFooter,
  TooltipTrigger,
  Tooltip,
  TooltipProvider,
  TooltipContent,
} from "@/components/ui";
import { AssignBOM } from "./AssignBom";
import {
  END_DATE,
  START_DATE,
  STATUS,
  statusOptions,
  BOM_POSITION_ID,
} from "./consts";
import { FormProvider } from "react-hook-form";
import { SwapDialog } from "./SwapDialog";
import moment from "moment";
import { Info } from "lucide-react";

const BOMs = () => {
  const {
    columns,
    onAddBOM,
    isAdd,
    onCancel,
    editFrom,
    onSubmitUpdateBOM,
    isUpdateBomUserPending,
    onCloseDialog,
    open,
    setOpen,
    requiredEditBomFields,
    isView,
    openDeleteDialog,
    setOpenDeleteDialog,
    onDeleteConfirm,
    isDeleteBomUserPending,
    swapConfirmDialog,
    setSwapConfirmDialog,
    swapBOMDetails,
    BOMList,
    bomPositions,
    isBOMPositionsLoading,
    onRowClick,
    deletedRow,
    isPropertyAndUserExists,
  } = useBOMs();

  const { errors } = editFrom.formState;

  return (
    <div className="mt-4">
      {/* Header & Search */}
      {isAdd ? (
        <AssignBOM onCancel={onCancel} />
      ) : (
        <>
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-end">
              {isPropertyAndUserExists ? (
                <></>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="mr-1" type="button">
                      <Info className="h-6 w-6 stroke-yellow-600" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>There are no owners associated with this community.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button
                onClick={onAddBOM}
                className="px-2"
                disabled={!isPropertyAndUserExists}
              >
                + Assign BM
              </Button>
            </div>
            {/* need to changes data to data?.data */}
            <DataTable
              columns={columns}
              data={(BOMList?.data || []).slice().sort((a: any, b: any) => {
                return (
                  (b.active === true ? 1 : 0) - (a.active === true ? 1 : 0)
                );
              })}
              onRowClick={onRowClick}
            />
          </div>

          {/* Edit View Dialog */}
          {open && (
            <Dialog
              open={open}
              setOpen={setOpen}
              title={isView ? "View BM Position" : "Edit BM Position"}
              hideFooter
            >
              {isBOMPositionsLoading ? (
                <Loader />
              ) : (
                <FormProvider {...editFrom}>
                  <form onSubmit={editFrom.handleSubmit(onSubmitUpdateBOM)}>
                    <div className="grid grid-cols-1 gap-[30px]">
                      <FormSelect
                        name={BOM_POSITION_ID}
                        control={editFrom.control}
                        label="Position"
                        placeholder="Please select"
                        error={errors[BOM_POSITION_ID]?.message}
                        isRequired={requiredEditBomFields.includes(
                          BOM_POSITION_ID
                        )}
                        options={
                          bomPositions?.data?.map((position: any) => ({
                            label: position.positionName,
                            value: position.bomPositionId?.toString(),
                          })) || []
                        }
                        disabled={isView} // if position is change call this API: /api/v1/bom-users POST, except: bomPositionId
                      />
                      <FormDatePicker
                        name={START_DATE}
                        control={editFrom.control}
                        label="Start Date"
                        placeholder="Please select"
                        error={errors[START_DATE]?.message}
                        isRequired={true}
                        disabled={isView}
                        maxDate={new Date()}
                        onClear={() => editFrom.setValue(END_DATE, "")}
                        onChange={(date: any) => {
                          if (
                            moment(date).isAfter(editFrom.getValues()[END_DATE])
                          ) {
                            editFrom.setValue(END_DATE, "");
                          }
                        }}
                      />
                      <FormDatePicker
                        name={END_DATE}
                        control={editFrom.control}
                        label="End Date"
                        placeholder="Please select"
                        error={errors[END_DATE]?.message}
                        isRequired={requiredEditBomFields.includes(END_DATE)}
                        disabled={isView || !editFrom.watch(START_DATE)}
                        minDate={new Date(editFrom.watch(START_DATE))}
                      />
                      <FormSelect
                        name={STATUS}
                        control={editFrom.control}
                        label="Status"
                        placeholder="Please select"
                        error={errors[STATUS]?.message}
                        isRequired={requiredEditBomFields.includes(STATUS)}
                        options={statusOptions}
                        disabled={isView}
                      />
                    </div>
                    <DialogFooter className="mt-5">
                      <DialogClose>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isUpdateBomUserPending}
                          onClick={onCloseDialog}
                        >
                          Cancel
                        </Button>
                      </DialogClose>
                      {!isView && (
                        <Button type="submit" disabled={isUpdateBomUserPending}>
                          {isUpdateBomUserPending && (
                            <Loader variant="secondary" />
                          )}{" "}
                          Submit
                        </Button>
                      )}
                    </DialogFooter>
                  </form>
                </FormProvider>
              )}
            </Dialog>
          )}

          {/* Delete Confirmation Dialog */}
          {openDeleteDialog && (
            <Dialog
              open={openDeleteDialog}
              setOpen={setOpenDeleteDialog}
              title={
                <div className="text-2xl font-bold text-primary">
                  {"Confirmation"}
                  <div className="h-1 w-[30%] bg-primary rounded-full" />
                </div>
              }
              saveText="Delete"
              saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
              isLoading={isDeleteBomUserPending}
              onSave={onDeleteConfirm}
            >
              <p className="text-sm">
                Are you sure you want to delete the BM User named{" "}
                <b className="text-primary">{`${deletedRow?.firstName} ${deletedRow?.firstName}`}</b>{" "}
                with Position{" "}
                <b className="text-primary">{`${deletedRow?.positionName}`}</b>?
              </p>
            </Dialog>
          )}

          {/* Swap BM Confirmation Dialog */}
          <SwapDialog
            swapConfirmDialog={swapConfirmDialog}
            setSwapConfirmDialog={setSwapConfirmDialog}
            swapBOMDetails={swapBOMDetails}
          />
        </>
      )}
    </div>
  );
};

export default BOMs;
